import { Link } from "react-router";

import { Emblem } from "@/components/brand/Emblem";
import { AmbientBackdrop } from "@/components/layout/AmbientBackdrop";
import { TopBar } from "@/components/layout/TopBar";
import { KeyIcon, PassportIcon, ShieldIcon } from "@/components/ui/icons";

import { LoginForm } from "./LoginForm";
import { RegisterForm } from "./RegisterForm";

type AuthMode = "login" | "register";

const COPY: Record<AuthMode, { title: string; subtitle: string }> = {
  login: {
    title: "Welcome back to Coal City",
    subtitle: "Your citizen, your home and your street legacy are waiting.",
  },
  register: {
    title: "Become a citizen",
    subtitle: "Claim your passport. You start in a trench house — the rest of Enugu is yours to earn.",
  },
};

const TABS: { mode: AuthMode; to: string; label: string; icon: typeof KeyIcon }[] = [
  { mode: "login", to: "/login", label: "Citizen login", icon: KeyIcon },
  { mode: "register", to: "/register", label: "New passport", icon: PassportIcon },
];

/** Login and registration share one panel, switched by tabs (Stitch: Citizen Authentication). */
export function AuthPage({ mode }: { mode: AuthMode }) {
  const copy = COPY[mode];

  return (
    <div className="relative flex min-h-full flex-col overflow-hidden">
      <AmbientBackdrop />
      <TopBar>
        <Link
          to="/system"
          className="rounded-full px-3 py-1.5 text-label-md uppercase text-on-surface-variant transition hover:bg-surface-container-high hover:text-on-surface"
        >
          System status
        </Link>
      </TopBar>

      <main className="relative z-10 flex flex-1 items-center justify-center px-4 pb-12 pt-4">
        <section className="glass-panel relative w-full max-w-[480px] px-6 py-8 sm:px-10 sm:py-10">
          <div className="absolute inset-x-10 top-0 h-px bg-gradient-to-r from-transparent via-primary-container to-transparent" />

          <div className="flex flex-col items-center text-center">
            <div className="rounded-2xl shadow-glow-primary">
              <Emblem className="size-16" />
            </div>
            <h1 className="mt-5 text-headline-lg font-extrabold uppercase tracking-tight">
              {copy.title}
            </h1>
            <p className="mt-2 max-w-sm text-body-md text-on-surface-variant">{copy.subtitle}</p>
          </div>

          <nav
            aria-label="Account"
            className="mt-7 grid grid-cols-2 gap-1 rounded-xl bg-surface-container-lowest p-1"
          >
            {TABS.map(({ mode: tabMode, to, label, icon: Icon }) => {
              const active = tabMode === mode;
              return (
                <Link
                  key={tabMode}
                  to={to}
                  replace
                  aria-current={active ? "page" : undefined}
                  className={`flex h-12 items-center justify-center gap-2 rounded-lg text-label-lg uppercase transition ${
                    active
                      ? "border border-primary-container/40 bg-surface-container-high text-primary shadow-glow-primary"
                      : "text-on-surface-variant hover:text-on-surface"
                  }`}
                >
                  <Icon className="size-4" />
                  {label}
                </Link>
              );
            })}
          </nav>

          {mode === "login" ? <LoginForm /> : <RegisterForm />}

          <footer className="mt-8 flex items-start gap-2 border-t border-outline-variant/50 pt-5 text-body-sm text-on-surface-variant">
            <ShieldIcon className="mt-0.5 size-4 shrink-0 text-primary" />
            <p>
              Coal Cash (<span className="text-secondary">₦C</span>) is a virtual in-game currency
              with no real-world monetary value.
            </p>
          </footer>
        </section>
      </main>
    </div>
  );
}